import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useApi } from "./ApiContext";

const DesignContext = createContext();

// Default designs for components
const defaultDesigns = {
	profileHeader: {
		statsValueFont: {
			fontFamily: "Helvetica",
			fontSize: 14,
			fontWeight: "600",
			color: "#000000",
		},
		statsLabelFont: {
			fontFamily: "Helvetica",
			fontSize: 12,
			fontWeight: "400",
			color: "#8E8E93",
		},
		tabsSharedStyles: {
			showIcon: true,
			showLabel: true,
			backgroundColor: "#FFFFFF",
			fontFamily: "Helvetica",
			fontSize: 14,
			fontWeight: "400",
			color: "#2a2a2a",
		},
	},
	quickAccess: {
		fabBackgroundColor: "#FFFFFF",
		fabIconColor: "#007AFF",
		fabShadowColor: "rgba(0, 0, 0, 0.2)",
		fabItemBackgroundColor: "#FFFFFF",
		fabItemTextColor: "#2A2A2A",
		fabItemIconColor: "#007AFF",
	},
	// Add other component designs here
};

export const DesignProvider = ({ children }) => {
	const [designs, setDesigns] = useState(defaultDesigns);
	const { api } = useApi();

	const fetchDesign = useCallback(
		(componentType) => {
			// First check if component exists in current state
			if (!designs[componentType]) {
				// If not in current state, return default design
				return defaultDesigns[componentType] || null;
			}

			// Component exists in current state - for top level values that might be
			// missing, selectively add them from defaults without overriding anything
			const currentComponentDesign = designs[componentType];
			const defaultComponentDesign = defaultDesigns[componentType] || {};

			// Only hydrate missing top-level properties - not arrays or deeply nested objects
			// This preserves user customizations like array length changes
			const hydratedDesign = { ...currentComponentDesign };

			// Add any missing top-level properties from defaults
			Object.keys(defaultComponentDesign).forEach((key) => {
				// If property is missing entirely, add it from defaults
				if (hydratedDesign[key] === undefined) {
					hydratedDesign[key] = defaultComponentDesign[key];
				}
			});

			return hydratedDesign;
		},
		[designs]
	);

	const designSnapshot = useCallback(() => {
		return designs;
	}, [designs]);

	const designRestore = useCallback((newDesign) => {
		setDesigns(newDesign);
	}, []);

	const updateDesign = useCallback((componentType, newDesign) => {
		setDesigns((prev) => {
			// Get the component design, ensuring hydration
			const currentComponent = prev[componentType] || {};
			const defaultComponentDesign = defaultDesigns[componentType] || {};

			// Create a base that includes defaults for any missing properties
			const baseDesign = { ...currentComponent };
			Object.keys(defaultComponentDesign).forEach((key) => {
				if (baseDesign[key] === undefined) {
					baseDesign[key] = defaultComponentDesign[key];
				}
			});

			// Apply the new design updates
			return {
				...prev,
				[componentType]: {
					...baseDesign,
					...newDesign,
				},
			};
		});
	}, []);

	return (
		<DesignContext.Provider
			value={{
				designs,
				fetchDesign,
				updateDesign,
				designSnapshot,
				designRestore,
			}}>
			{children}
		</DesignContext.Provider>
	);
};

export const useDesign = () => {
	const context = useContext(DesignContext);
	if (!context) {
		throw new Error("useDesign must be used within a DesignProvider");
	}
	return context;
};
